import React, { Component } from 'react';
import PropTypes from 'prop-types';

import UploadArea from '@components/common/filestack_uploader/upload_area';
import {
  handleCheckedStatus, createCheckinCheckoutEvent, formatVersions, joinVersionEvents
} from '@helpers/versions_helper';
import { BFLoader } from '@components/common/loader/main';
import getReservations, { postReservations } from '@api/v4/attachments/reservations';
import getVersions, { rollbackVersions } from '@api/v4/attachments/versions';
import CheckinCheckout from './checkin_checkout';
import TimelineEvent from './timeline_event';


export default class StatusContent extends Component {
  state = {
    checkinCheckoutUpdating: false,
    currentVersionUpdating: false,
    fetchControllers: [],
    isLoading: false,
    joinedEvents: [],
    versionDownloading: false,
    versionUploading: false
  }

  componentDidMount() {
    this.createVersionEvents();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.visibleAttachment !== this.props.visibleAttachment) {
      this.abortFetchControllers();
      this.createVersionEvents();
    }
  }

  componentWillUnmount() {
    this.abortFetchControllers();
  }

  updateFetchControllers = (controller) => {
    this.setState((prevState) => ({ fetchControllers: [...prevState.fetchControllers, controller] }));
  }

  abortFetchControllers = () => {
    this.state.fetchControllers.forEach((controller) => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
  }

  makeCurrentVersion = (versionKey) => {
    if (this.state.currentVersionUpdating) { return; }

    this.setState({ currentVersionUpdating: true, isLoading: true });

    rollbackVersions({ versionKey }, this.updateFetchControllers)
      .then((response) => {
        if (response.data) {
          this.props.updateSideBarResources(response.data); // update attachment versions
        }
        this.setState({ currentVersionUpdating: false });
      });
  }

  createVersionEvents = () => {
    const versionsPromise = this.retrieveAttachmentVersions();
    const reservationsPromise = this.retrieveAttachmentReservations();

    Promise.all([versionsPromise, reservationsPromise]).then((responses) => {
      const versions = responses[0];
      const reservations = responses[1];
      const processedVersions = formatVersions(versions);
      const joinedEvents = joinVersionEvents(processedVersions, reservations);

      this.setState({
        isLoading: false,
        joinedEvents
      });
    });
  }

  retrieveAttachmentVersions = () => {
    const attachmentKey = this.props.visibleAttachment.id;

    this.setState({ isLoading: true });

    return getVersions({ attachmentKey }, this.updateFetchControllers)
      .then((response) => {
        if (!response.data) {
          this.setState({ isLoading: false });
        }
        return response;
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  retrieveAttachmentReservations = () => {
    const attachmentKey = this.props.visibleAttachment.id;
    const options = { attachmentKey, params: { include: 'user' } };

    return getReservations(options, this.updateFetchControllers)
      .then((response) => {
        if (!response.data) {
          this.setState({ isLoading: false });
        }
        return response;
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  toggleCheckinCheckout = (checkinCheckoutNote) => {
    const { joinedEvents } = this.state;
    const attachmentKey = this.props.visibleAttachment.id;
    const checkedinStatus = !!handleCheckedStatus(joinedEvents).checkedinStatus;
    const updatedStatus = checkedinStatus ? 'checked_out' : 'checked_in';
    const options = {
      attachmentKey,
      checkinCheckoutNote,
      updatedStatus,
      params: { include: 'user' }
    };

    this.setState({ checkinCheckoutUpdating: true });

    postReservations(options, this.updateFetchControllers)
      .then((response) => {
        if (response && !response.ok) {
          const updatedJoinedEvents = [...joinedEvents];
          const baseEvent = updatedJoinedEvents[0];
          const newEvent = createCheckinCheckoutEvent(response, response.data, baseEvent);
          updatedJoinedEvents.unshift(newEvent);

          this.setState({
            checkinCheckoutUpdating: false,
            joinedEvents: updatedJoinedEvents,
          });
        } else {
          this.setState({ checkinCheckoutUpdating: false });
        }
      })
      .catch(() => this.setState({ checkinCheckoutUpdating: false }));
  }

  renderEvents() {
    if (this.state.isLoading) { return undefined; }

    const { joinedEvents, versionDownloading, versionUploading } = this.state;

    return joinedEvents.map((event) => (
      <TimelineEvent
        key={event.updatedAt}
        makeCurrentVersion={this.makeCurrentVersion}
        version={event}
        versionDownloading={versionDownloading}
        versionUploading={versionUploading}
      />
    ));
  }

  renderUploader() {
    if (BFG.hasFeature('uppy_uploader_features')) { return null; }
    const { asset, updateSideBarResources, visibleAttachment } = this.props;
    const uploadOptions = {
      maxFiles: 1,
      onFileUploadStarted: () => this.setState({ versionUploading: true })
    };
    return (
      <UploadArea
        asset={asset}
        assetKey={asset.asset_key}
        attachment={visibleAttachment}
        options={uploadOptions}
        uniqDomElement={`version-status-uploader-${visibleAttachment.id}`}
        updateTab={updateSideBarResources}
        updateVersionUploading={(state) => this.setState(state)}
        uploadType="attachment-version-status"
      />
    );
  }

  render() {
    const { checkinCheckoutUpdating, isLoading, joinedEvents } = this.state;
    const { asset, updateSideBarResources, visibleAttachment } = this.props;

    return (
      <div className="status-content-container">
        <CheckinCheckout
          asset={asset}
          checkinCheckoutUpdating={checkinCheckoutUpdating}
          joinedEvents={joinedEvents}
          isLoading={isLoading}
          toggleCheckinCheckout={this.toggleCheckinCheckout}
          updateTab={updateSideBarResources}
          updateVersionUploading={(state) => this.setState(state)}
          visibleAttachment={visibleAttachment}
        />
        <div className="timeline-header-container">
          <h3>{t('Version Timeline')}</h3>
          {this.renderUploader()}
        </div>
        <div className="timeline-events">
          {isLoading ? <BFLoader /> : null}
          {this.renderEvents()}
        </div>
      </div>
    );
  }
}

StatusContent.propTypes = {
  asset: PropTypes.shape({
    asset_key: PropTypes.string
  }).isRequired,
  updateSideBarResources: PropTypes.func.isRequired,
  visibleAttachment: PropTypes.shape({
    id: PropTypes.string
  }).isRequired
};
